import React, { useCallback, useEffect, useRef, useState } from 'react';

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const DISMISS_THRESHOLD_PX = 100;
const DOUBLE_TAP_MS = 280;
const DOUBLE_TAP_SCALE = 2.5;
const GESTURE_LOCK_PX = 8;

function touchDistance(touches) {
  if (touches.length < 2) return 0;
  const dx = touches[0].clientX - touches[1].clientX;
  const dy = touches[0].clientY - touches[1].clientY;
  return Math.hypot(dx, dy);
}

function clampScale(scale) {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
}

/**
 * iPhone-style pinch-zoom image with drag-down-to-dismiss when at 1x zoom.
 */
export default function MobileMediaViewer({
  src,
  alt = '',
  className = '',
  onDismiss,
  onDismissProgress,
  onSwipeHorizontal,
}) {
  const rootRef = useRef(null);
  const transformRef = useRef({ scale: 1, x: 0, y: 0 });
  const gestureRef = useRef(null);
  const lastTapRef = useRef(0);
  const [transform, setTransform] = useState({ scale: 1, x: 0, y: 0 });
  const [dismissY, setDismissY] = useState(0);
  const dismissYRef = useRef(0);

  const applyTransform = useCallback((next) => {
    transformRef.current = next;
    setTransform(next);
  }, []);

  const applyDismissY = useCallback(
    (next) => {
      dismissYRef.current = next;
      setDismissY(next);
      onDismissProgress?.(next);
    },
    [onDismissProgress]
  );

  useEffect(() => {
    applyTransform({ scale: 1, x: 0, y: 0 });
    applyDismissY(0);
    gestureRef.current = null;
  }, [src, applyTransform, applyDismissY]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const resetDismiss = () => {
      applyDismissY(0);
    };

    const snapDismissBack = () => {
      applyDismissY(0);
    };

    const onTouchStart = (e) => {
      if (e.touches.length === 2) {
        gestureRef.current = {
          mode: 'pinch',
          startDist: touchDistance(e.touches),
          startScale: transformRef.current.scale,
          startX: transformRef.current.x,
          startY: transformRef.current.y,
        };
        e.stopPropagation();
        return;
      }

      if (e.touches.length === 1) {
        const touch = e.touches[0];
        if (transformRef.current.scale > 1.01) {
          gestureRef.current = {
            mode: 'pan',
            startX: transformRef.current.x,
            startY: transformRef.current.y,
            touchX: touch.clientX,
            touchY: touch.clientY,
          };
          e.stopPropagation();
          return;
        }

        gestureRef.current = {
          mode: 'undecided',
          touchX: touch.clientX,
          touchY: touch.clientY,
          startDismissY: dismissYRef.current,
        };
      }
    };

    const onTouchMove = (e) => {
      const gesture = gestureRef.current;
      if (!gesture) return;

      if (gesture.mode === 'pinch' && e.touches.length >= 2) {
        const dist = touchDistance(e.touches);
        if (!gesture.startDist) return;
        const nextScale = clampScale(gesture.startScale * (dist / gesture.startDist));
        applyTransform({
          scale: nextScale,
          x: transformRef.current.x,
          y: transformRef.current.y,
        });
        if (nextScale > 1.01) resetDismiss();
        if (e.cancelable) e.preventDefault();
        e.stopPropagation();
        return;
      }

      if (gesture.mode === 'pan' && e.touches.length === 1) {
        const dx = e.touches[0].clientX - gesture.touchX;
        const dy = e.touches[0].clientY - gesture.touchY;
        applyTransform({
          scale: transformRef.current.scale,
          x: gesture.startX + dx,
          y: gesture.startY + dy,
        });
        if (e.cancelable) e.preventDefault();
        e.stopPropagation();
        return;
      }

      if (gesture.mode === 'undecided' && e.touches.length === 1) {
        const dx = e.touches[0].clientX - gesture.touchX;
        const dy = e.touches[0].clientY - gesture.touchY;

        if (Math.abs(dx) < GESTURE_LOCK_PX && Math.abs(dy) < GESTURE_LOCK_PX) return;

        if (Math.abs(dy) > Math.abs(dx) && dy > 0) {
          gestureRef.current = { mode: 'dismiss', touchY: gesture.touchY, startDismissY: gesture.startDismissY };
        } else if (Math.abs(dx) > Math.abs(dy) && onSwipeHorizontal) {
          gestureRef.current = { mode: 'swipe', touchX: gesture.touchX, touchY: gesture.touchY };
        } else {
          gestureRef.current = null;
          return;
        }
      }

      const activeGesture = gestureRef.current;
      if (!activeGesture) return;

      if (activeGesture.mode === 'dismiss' && e.touches.length === 1) {
        const dy = Math.max(0, e.touches[0].clientY - activeGesture.touchY);
        applyDismissY(dy);
        if (e.cancelable) e.preventDefault();
        e.stopPropagation();
        return;
      }

      if (activeGesture.mode === 'swipe' && e.touches.length === 1 && onSwipeHorizontal) {
        const dx = e.touches[0].clientX - activeGesture.touchX;
        const dy = e.touches[0].clientY - activeGesture.touchY;
        applyDismissY(0);
        if (Math.abs(dx) > Math.abs(dy)) {
          if (e.cancelable) e.preventDefault();
          e.stopPropagation();
        }
      }
    };

    const onTouchEnd = (e) => {
      const gesture = gestureRef.current;

      if (gesture?.mode === 'dismiss') {
        if (dismissYRef.current >= DISMISS_THRESHOLD_PX) {
          onDismiss?.();
        } else {
          snapDismissBack();
        }
        gestureRef.current = null;
        return;
      }

      if (gesture?.mode === 'swipe' && e.changedTouches.length === 1 && onSwipeHorizontal) {
        const touch = e.changedTouches[0];
        const dx = touch.clientX - gesture.touchX;
        const dy = touch.clientY - gesture.touchY;
        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) >= 56) {
          onSwipeHorizontal(dx < 0 ? 'left' : 'right');
        }
        gestureRef.current = null;
        return;
      }

      gestureRef.current = null;

      if (transformRef.current.scale <= 1.01) {
        applyTransform({ scale: 1, x: 0, y: 0 });
      }
    };

    const onDoubleTap = (e) => {
      const now = Date.now();
      if (now - lastTapRef.current < DOUBLE_TAP_MS) {
        e.preventDefault();
        if (transformRef.current.scale > 1.01) {
          applyTransform({ scale: 1, x: 0, y: 0 });
          resetDismiss();
        } else {
          applyTransform({ scale: DOUBLE_TAP_SCALE, x: 0, y: 0 });
        }
        lastTapRef.current = 0;
        return;
      }
      lastTapRef.current = now;
    };

    root.addEventListener('touchstart', onTouchStart, { passive: true });
    root.addEventListener('touchmove', onTouchMove, { passive: false });
    root.addEventListener('touchend', onTouchEnd, { passive: true });
    root.addEventListener('touchcancel', onTouchEnd, { passive: true });
    root.addEventListener('click', onDoubleTap);

    return () => {
      root.removeEventListener('touchstart', onTouchStart);
      root.removeEventListener('touchmove', onTouchMove);
      root.removeEventListener('touchend', onTouchEnd);
      root.removeEventListener('touchcancel', onTouchEnd);
      root.removeEventListener('click', onDoubleTap);
    };
  }, [src, applyTransform, applyDismissY, onDismiss, onSwipeHorizontal]);

  const dismissScale = Math.max(0.82, 1 - dismissY / 900);
  const combinedY = transform.y + dismissY;

  return (
    <div
      ref={rootRef}
      className={`mobile-media-viewer ${className}`.trim()}
      style={{
        opacity: dismissY > 0 ? Math.max(0.35, 1 - dismissY / 320) : 1,
      }}
    >
      <img
        src={src}
        alt={alt}
        className="mobile-media-viewer__img"
        style={{
          transform: `translate3d(${transform.x}px, ${combinedY}px, 0) scale(${transform.scale * dismissScale})`,
        }}
        draggable={false}
      />
    </div>
  );
}
